/**
 * LA PÁGINA DOCUMENTACIÓN: preparar lo que manda `GET /admin/documentacion` para pintarlo
 * (fase 4 de «la app sin ActiveCampaign», 07-10).
 *
 * El backend ya lo manda redactado (reglas/documentacion.py). Aquí solo se filtra, se agrupa
 * y se ordena: ninguna frase se redacta en el navegador.
 */

// Sin tildes y en minúsculas, para que «quincenal» encuentre «Quincenal» y «automatizacion»
// encuentre «automatización».
export const sinTildes = (texto) => String(texto ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Todo lo que se puede buscar de una regla, en una sola línea.
const textoDeLaRegla = (r) => sinTildes([
    r.titulo, r.frase, r.fuente, r.notas, ...(r.ejemplos || []),
    ...(r.detalles?.llega_a || []),
    ...(r.correos || []).flatMap((c) => [c.nombre, c.asunto, c.cuerpo]),
    ...(r.tareas || []),
    ...(r.avisos || []).flatMap((a) => [a.titulo, a.cuerpo]),
    ...(r.ademas || []),
].filter(Boolean).join(' '));

/**
 * ¿Casa la regla con lo que se busca? Un número («9», «R9», «regla 9») busca ESA regla y no
 * la 19 ni la 29. Varias palabras tienen que estar todas.
 */
export const casaLaRegla = (regla, busqueda) => {
    const q = sinTildes(busqueda).trim();
    if (!q) return true;
    const numero = q.match(/^(?:r|regla\s*)?(\d+)$/);
    if (numero) return regla.numero === Number(numero[1]);
    const texto = textoDeLaRegla(regla);
    return q.split(/\s+/).every((palabra) => texto.includes(palabra));
};

/** Los bloques con las reglas que casan; los que se quedan vacíos no salen. */
export const filtrarBloques = (bloques, busqueda) => (Array.isArray(bloques) ? bloques : [])
    .map((b) => ({ ...b, reglas: (b.reglas || []).filter((r) => casaLaRegla(r, busqueda)) }))
    .filter((b) => b.reglas.length > 0);

export const contarReglas = (bloques) => (Array.isArray(bloques) ? bloques : [])
    .reduce((n, b) => n + (b.reglas || []).length, 0);

/** «aplicada 3 veces esta semana». Sin dato (el reloj no manda, o no se pudo contar), nada. */
export const vecesEstaSemana = (n) => {
    if (typeof n !== 'number' || Number.isNaN(n)) return null;
    if (n === 0) return 'No se ha aplicado esta semana';
    return `Aplicada ${n} ${n === 1 ? 'vez' : 'veces'} esta semana`;
};

/**
 * Lo que se dice de una regla sobre esta semana. De las reglas que no dejan rastro en el
 * historial (o que no ejecutan las máquinas) NO se puede decir «no se ha aplicado»: el backend
 * lo marca en `reloj.sin_cuenta`, con su porqué, y aquí sale su texto en vez de un cero falso.
 */
export const cuentaDeLaSemana = (regla) => {
    const sinCuenta = regla?.reloj?.sin_cuenta;
    if (sinCuenta?.chapa) return { texto: sinCuenta.chapa, seCuenta: false };
    const texto = vecesEstaSemana(regla?.aplicada_esta_semana);
    return texto ? { texto, seCuenta: true } : null;
};

/** «2026-10-06» → «06-10-2026». Lo que no sea una fecha se deja como viene. */
export const fechaDelCatalogo = (iso) => {
    const m = String(iso ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : String(iso ?? '');
};

/** La línea de arriba: qué versión de cada fichero se está leyendo. */
export const versionDelCatalogo = (cabecera) => {
    const c = cabecera || {};
    const uno = (nombre, v) => (v?.version ? `${nombre} v${v.version} (${fechaDelCatalogo(v.fecha)})` : null);
    return [uno('Reglas y planes', c.reglas), uno('Textos de los correos', c.correos)].filter(Boolean);
};

const planes = (lista) => (Array.isArray(lista) ? lista : []);

/** Los planes que casan con la búsqueda (por nombre, estado o tipo de ciclo). */
export const filtrarPlanes = (lista, busqueda) => {
    const q = sinTildes(busqueda).trim();
    if (!q) return planes(lista);
    return planes(lista).filter((p) => sinTildes(`${p.nombre} ${p.estado} ${p.ciclo}`).includes(q));
};

/**
 * EL DIBUJO DE UN CICLO. El backend manda una fila por largo de ciclo (`diagrama.ciclos`: una
 * en el de cuatro semanas; tres, de 4, 5 y 6, en el de Premium), cada una con sus semanas y,
 * por regla, de qué semanas sale DE VERDAD y a cuál lleva. De una fila se saca lo que se pinta:
 *
 *   semanas    las semanas en fila, cada una con las reglas que actúan DENTRO de ella sin
 *              moverla y la flecha a la siguiente (las reglas que la pasan a la de al lado)
 *   saltos     los cambios de semana que no son «a la siguiente»: la vuelta a la 1, la
 *              entrada en la 0...
 *   generales  las reglas que no cambian de semana y valen en cualquiera
 */
export const dibujoDelCiclo = (ciclo) => {
    const estados = (ciclo?.estados || []).filter((e) => /^Semana \d+$/.test(e));
    const reglas = ciclo?.reglas || [];
    const indice = (e) => estados.indexOf(e);
    const semanas = estados.map((nombre) => ({ nombre, dentro: [], siguiente: [] }));
    const saltos = [];
    const generales = [];
    reglas.forEach((r) => {
        if (!r.cambia_de_semana) {
            // Actúa sin mover la semana: dentro de las suyas, o en cualquiera.
            const suyas = (r.de || []).filter((e) => indice(e) >= 0);
            if (r.desde_cualquiera || suyas.length === 0) generales.push(r);
            else suyas.forEach((e) => semanas[indice(e)].dentro.push(r));
            return;
        }
        const pares = r.pares || [];
        const aLaSiguiente = !r.desde_cualquiera && pares.length > 0
            && pares.every(([de, a]) => indice(de) >= 0 && indice(a) === indice(de) + 1);
        if (aLaSiguiente) pares.forEach(([de]) => semanas[indice(de)].siguiente.push(r));
        else saltos.push(r);
    });
    return { semanas, saltos, generales };
};

/** Las filas que se pintan de un diagrama: una por largo de ciclo, con su título si lo trae. */
export const filasDelDiagrama = (diagrama) => (diagrama?.ciclos || [])
    .map((ciclo) => ({ titulo: ciclo.titulo || null, ...dibujoDelCiclo(ciclo) }));

/**
 * Las reglas de un diagrama, en dos listas para leerlas debajo del dibujo: las que cambian la
 * semana y las que actúan sin cambiarla. El recorrido y las condiciones de cada una vienen ya
 * redactados del backend (con la semana real de cada largo de ciclo): aquí solo se reparten.
 */
export const listasDelDiagrama = (diagrama) => {
    const reglas = diagrama?.reglas || [];
    return {
        cambian: reglas.filter((r) => r.cambia_de_semana),
        noCambian: reglas.filter((r) => !r.cambia_de_semana),
    };
};

/** Los números de unas reglas, para decirlos en una frase: «la regla 3», «las reglas 3 y 4». */
export const reglasEnClaro = (numeros) => {
    const n = (Array.isArray(numeros) ? numeros : []).filter((x) => typeof x === 'number');
    if (n.length === 0) return '';
    if (n.length === 1) return `la regla ${n[0]}`;
    return `las reglas ${n.slice(0, -1).join(', ')} y ${n[n.length - 1]}`;
};
